import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { sbGetSetting, sbSetSetting } from "../utils/supabaseClient";
import { sendNotification } from "../utils/notifications";
import { useToast } from "./Toast";
import { renderLessonContentWithMarkdown } from "../utils/lessonMarkdown";
import PreviewModal from "./PreviewModal";
import PullToRefresh from "./PullToRefresh";
import TablerIconPicker, { SVG_ICONS } from "./TablerIconPicker";
import "../styles/ListTab.css";

const LessonListTab = forwardRef(({ onHeaderInfoChange }, ref) => {
  const { success, error } = useToast();
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTag, setSelectedTag] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState(null);
  const [previewTitle, setPreviewTitle] = useState("");
  const [existingTags, setExistingTags] = useState([]);
  const [editingCategoryMode, setEditingCategoryMode] = useState("select");

  useEffect(() => {
    loadLessons();
  }, []);

  useImperativeHandle(ref, () => ({
    loadLessons
  }));

  useEffect(() => {
    onHeaderInfoChange?.({ title: "Lessons", count: lessons.length });
  }, [lessons, onHeaderInfoChange]);

  const loadLessons = async () => {
    try {
      setLoading(true);
      const data = await sbGetSetting("origamic-lessons");
      const sorted = Array.isArray(data)
        ? [...data].sort((a, b) => new Date(b.addedAt) - new Date(a.addedAt))
        : [];
      setLessons(sorted);
    } catch (err) {
      console.error("Error loading lessons:", err);
      error("Failed to load lessons");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this lesson?")) return;

    try {
      const updated = lessons.filter((l) => l.id !== id);
      await sbSetSetting("origamic-lessons", updated);
      setLessons(updated);
      success("Lesson deleted");
    } catch (err) {
      console.error("Error deleting lesson:", err);
      error("Failed to delete lesson");
    }
  };

  const handleEdit = async (lesson) => {
    setEditingId(lesson.id);
    setEditFormData({ ...lesson });
    setEditingCategoryMode("select");

    try {
      const allLessons = await sbGetSetting("origamic-lessons");
      if (Array.isArray(allLessons)) {
        const tags = [...new Set(allLessons.map(l => l.tag).filter(Boolean))].sort();
        setExistingTags(tags);
      }
    } catch (err) {
      console.error("Error loading tags:", err);
    }
  };

  const handleSaveEdit = async () => {
    try {
      const oldLesson = lessons.find((l) => l.id === editingId);
      const wasPublished = oldLesson?.draft && !editFormData.draft;

      const updated = lessons.map((l) =>
        l.id === editingId ? editFormData : l
      );
      await sbSetSetting("origamic-lessons", updated);

      if (wasPublished) {
        await sendNotification(
          `New Lesson: ${editFormData.title}`,
          editFormData.subtitle || "A new lesson is available to explore.",
          "learn",
          editFormData.id
        );
      }

      setLessons(updated);
      setEditingId(null);
      success("Lesson updated");
    } catch (err) {
      console.error("Error saving lesson:", err);
      error("Failed to save lesson");
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditFormData({});
    setEditingCategoryMode("select");
  };


  const handleToggleDraft = async (lesson) => {
    try {
      const newDraftStatus = !lesson.draft;
      // When publishing, set publishedDate if not already set
      const publishedDate = !newDraftStatus && !lesson.publishedDate ? new Date().toISOString() : lesson.publishedDate;

      const updated = lessons.map(l =>
        l.id === lesson.id ? { ...l, draft: newDraftStatus, publishedDate } : l
      );
      await sbSetSetting("origamic-lessons", updated);

      if (newDraftStatus === false) {
        // Publishing: send notification
        await sendNotification(
          `New Lesson: ${lesson.title}`,
          lesson.subtitle || "A new lesson is available to explore.",
          "learn",
          lesson.id
        );
      }

      setLessons(updated);
    } catch (err) {
      console.error("Error toggling draft status:", err);
      error("Failed to update draft status");
    }
  };

  const openPreview = (lessonData) => {
    const content = renderLessonContentWithMarkdown(lessonData);
    setPreviewTitle(`Lesson: ${lessonData.title}`);
    setPreviewContent(content);
    setPreviewOpen(true);
  };

  if (loading) return <div className="loading">Loading lessons...</div>;

  if (lessons.length === 0)
    return <div className="empty-state">No lessons found. Create one first!</div>;

  // Get unique tags from lessons
  const uniqueTags = [...new Set(lessons.map(lesson => lesson.tag).filter(Boolean))].sort();

  const filteredLessons = lessons.filter((lesson) => {
    // Filter by search query
    const matchesSearch =
      lesson.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lesson.subtitle?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lesson.tag?.toLowerCase().includes(searchQuery.toLowerCase());

    // Filter by selected tag
    const matchesTag = selectedTag === null || lesson.tag === selectedTag;

    // Filter by selected status (Published vs Draft)
    const matchesStatus = selectedStatus === null || (
      (selectedStatus === "published" && !lesson.draft) ||
      (selectedStatus === "draft" && lesson.draft)
    );

    return matchesSearch && matchesTag && matchesStatus;
  });

  return (
    <div className="list-container">
      <PreviewModal
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
        content={previewContent}
        title={previewTitle}
      />

      <input
        type="text"
        placeholder="Search lessons..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        style={{
          width: "100%",
          padding: "10px 12px",
          marginBottom: "12px",
          background: "#050e1a",
          border: "1px solid rgba(59, 130, 246, 0.2)",
          borderRadius: "6px",
          color: "#fff",
          fontSize: "14px",
          boxSizing: "border-box",
        }}
      />

      {/* Category Filter Pills */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "20px", flexWrap: "wrap" }}>
        <button
          onClick={() => setSelectedTag(null)}
          style={{
            padding: "6px 14px",
            background: selectedTag === null ? "rgba(59, 130, 246, 0.2)" : "rgba(59, 130, 246, 0.05)",
            color: selectedTag === null ? "#5B9EF0" : "#7A9CC4",
            border: selectedTag === null ? "1px solid rgba(59, 130, 246, 0.4)" : "1px solid rgba(59, 130, 246, 0.2)",
            borderRadius: "20px",
            cursor: "pointer",
            fontSize: "13px",
            fontWeight: "500",
            transition: "all 0.2s",
          }}
          onMouseEnter={(e) => {
            if (selectedTag !== null) {
              e.target.style.background = "rgba(59, 130, 246, 0.1)";
              e.target.style.borderColor = "rgba(59, 130, 246, 0.3)";
            }
          }}
          onMouseLeave={(e) => {
            if (selectedTag !== null) {
              e.target.style.background = "rgba(59, 130, 246, 0.05)";
              e.target.style.borderColor = "rgba(59, 130, 246, 0.2)";
            }
          }}
        >
          All
        </button>
        {uniqueTags.map((tag) => {
          const count = lessons.filter(lesson => lesson.tag === tag).length;
          return (
            <button
              key={tag}
              onClick={() => setSelectedTag(tag)}
              style={{
                padding: "6px 14px",
                background: selectedTag === tag ? "rgba(59, 130, 246, 0.2)" : "rgba(59, 130, 246, 0.05)",
                color: selectedTag === tag ? "#5B9EF0" : "#7A9CC4",
                border: selectedTag === tag ? "1px solid rgba(59, 130, 246, 0.4)" : "1px solid rgba(59, 130, 246, 0.2)",
                borderRadius: "20px",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: "500",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                if (selectedTag !== tag) {
                  e.target.style.background = "rgba(59, 130, 246, 0.1)";
                  e.target.style.borderColor = "rgba(59, 130, 246, 0.3)";
                }
              }}
              onMouseLeave={(e) => {
                if (selectedTag !== tag) {
                  e.target.style.background = "rgba(59, 130, 246, 0.05)";
                  e.target.style.borderColor = "rgba(59, 130, 246, 0.2)";
                }
              }}
            >
              {tag} ({count})
            </button>
          );
        })}
      </div>

      {/* Status Filter Pills */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "20px", flexWrap: "wrap" }}>
        <button
          onClick={() => setSelectedStatus(null)}
          style={{
            padding: "6px 14px",
            background: selectedStatus === null ? "rgba(59, 130, 246, 0.2)" : "rgba(59, 130, 246, 0.05)",
            color: selectedStatus === null ? "#5B9EF0" : "#7A9CC4",
            border: selectedStatus === null ? "1px solid rgba(59, 130, 246, 0.4)" : "1px solid rgba(59, 130, 246, 0.2)",
            borderRadius: "20px",
            cursor: "pointer",
            fontSize: "13px",
            fontWeight: "500",
            transition: "all 0.2s",
          }}
        >
          All
        </button>
        {["published", "draft"].map((status) => {
          const label = status.charAt(0).toUpperCase() + status.slice(1);
          return (
            <button
              key={status}
              onClick={() => setSelectedStatus(status)}
              style={{
                padding: "6px 14px",
                background: selectedStatus === status ? "rgba(59, 130, 246, 0.2)" : "rgba(59, 130, 246, 0.05)",
                color: selectedStatus === status ? "#5B9EF0" : "#7A9CC4",
                border: selectedStatus === status ? "1px solid rgba(59, 130, 246, 0.4)" : "1px solid rgba(59, 130, 246, 0.2)",
                borderRadius: "20px",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: "500",
                transition: "all 0.2s",
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {editingId ? (
        // Edit Form
        <div className="edit-form">
          <h3>Edit Lesson</h3>
          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              value={editFormData.title || ""}
              onChange={(e) =>
                setEditFormData({ ...editFormData, title: e.target.value })
              }
            />
          </div>
          <div className="form-group">
            <label>Icon</label>
            <TablerIconPicker
              value={editFormData.icon}
              onChange={(icon) =>
                setEditFormData({ ...editFormData, icon })
              }
            />
          </div>
          <div className="form-group">
            <label>Duration</label>
            <input
              type="text"
              value={editFormData.dur || ""}
              onChange={(e) =>
                setEditFormData({ ...editFormData, dur: e.target.value })
              }
            />
          </div>
          <div className="form-group">
            <label>Category/Tag</label>
            {editingCategoryMode === "select" ? (
              <select
                value={editFormData.tag || ""}
                onChange={(e) => {
                  if (e.target.value === "new") {
                    setEditingCategoryMode("add");
                  } else {
                    setEditFormData({ ...editFormData, tag: e.target.value });
                  }
                }}
              >
                <option value="">-- Select Category --</option>
                {existingTags.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
                <option value="new">+ Add New Category</option>
              </select>
            ) : (
              <>
                <input
                  type="text"
                  value={editFormData.tag || ""}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, tag: e.target.value })
                  }
                  placeholder="Enter new category name..."
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => {
                    setEditingCategoryMode("select");
                  }}
                  style={{
                    marginTop: "8px",
                    padding: "8px 12px",
                    background: "#7A9CC4",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "12px",
                  }}
                >
                  Back to Categories
                </button>
              </>
            )}
          </div>
          <div className="form-group">
            <label>Subtitle</label>
            <textarea
              value={editFormData.subtitle || ""}
              onChange={(e) =>
                setEditFormData({ ...editFormData, subtitle: e.target.value })
              }
              rows="3"
            />
          </div>
          <div className="form-group">
            <label>Scheduled Date (Optional)</label>
            <input
              type="date"
              value={editFormData.scheduled ? new Date(editFormData.scheduled).toISOString().slice(0, 10) : ""}
              onChange={(e) =>
                setEditFormData({ ...editFormData, scheduled: e.target.value ? new Date(e.target.value).toISOString() : null })
              }
              title="Schedule when this lesson should be published"
              style={{ width: "100%" }}
            />
            <small style={{ color: "#7A9CC4", marginTop: "4px", display: "block" }}>
              Optional: Schedule this lesson to be published on a future date
            </small>
          </div>
          <div className="form-group">
            <label>Content (Markdown)</label>
            <textarea
              value={typeof editFormData.body === "string" ? editFormData.body : ""}
              onChange={(e) => setEditFormData({ ...editFormData, body: e.target.value })}
              rows="12"
              placeholder="Enter lesson content in markdown format..."
              style={{
                width: "100%",
                padding: "12px",
                border: "1px solid rgba(59, 130, 246, 0.15)",
                borderRadius: "8px",
                background: "#050e1a",
                color: "white",
                fontFamily: "inherit",
                fontSize: "14px",
                boxSizing: "border-box",
              }}
            />

            {/* Markdown Formatting Guide */}
            <details style={{ marginTop: "20px", padding: "16px", background: "rgba(59, 130, 246, 0.05)", borderRadius: "8px", border: "1px solid rgba(59, 130, 246, 0.15)" }}>
              <summary style={{ cursor: "pointer", fontWeight: "600", color: "#5B9EF0", fontSize: "14px", userSelect: "none" }}>
                Markdown Formatting Guide
              </summary>
              <div style={{ marginTop: "12px", color: "#9DB4D4", fontSize: "13px", lineHeight: "1.6" }}>
                <h4 style={{ color: "#5B9EF0", marginTop: "12px", marginBottom: "8px" }}>Fence Types (Info Boxes)</h4>
                <ul style={{ marginLeft: "20px" }}>
                  <li><code>:::tip</code> - Green info box with 💡 icon</li>
                  <li><code>:::warning</code> - Orange warning box with ⚠️ icon</li>
                  <li><code>:::error</code> - Red error box with ❌ icon</li>
                  <li><code>:::info</code> - Blue info box with ℹ️ icon</li>
                  <li><code>:::button Label, URL</code> - Clickable button (orange)</li>
                </ul>

                <h4 style={{ color: "#5B9EF0", marginTop: "12px", marginBottom: "8px" }}>Basic Formatting</h4>
                <ul style={{ marginLeft: "20px" }}>
                  <li><code>**text**</code> - Bold</li>
                  <li><code>*text*</code> - Italic</li>
                  <li><code>`code`</code> - Inline code</li>
                  <li><code># Heading</code> - Large heading</li>
                  <li><code>- [ ]</code> - Checkbox (read-only)</li>
                  <li><code>[text](url)</code> - Link</li>
                </ul>

                <h4 style={{ color: "#5B9EF0", marginTop: "12px", marginBottom: "8px" }}>Examples</h4>
                <div style={{ background: "#050e1a", padding: "12px", borderRadius: "6px", overflow: "auto", marginTop: "8px", border: "1px solid rgba(59, 130, 246, 0.2)" }}>
                  <pre style={{ margin: "0", color: "#5B9EF0", fontSize: "12px" }}>
{`:::tip
This is a helpful tip
:::

:::button Get Started, https://example.com
:::

**Bold text** and *italic text*

- Bullet 1
- Bullet 2`}
                  </pre>
                </div>

                <p style={{ marginTop: "12px", color: "#7A9CC4", fontSize: "12px" }}>
                  📥 Download the full guide: <a href="/MARKDOWN_FORMATTING_GUIDE.md" style={{ color: "#5B9EF0", textDecoration: "underline" }}>MARKDOWN_FORMATTING_GUIDE.md</a>
                </p>
              </div>
            </details>
          </div>
          <div className="form-group">
            <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={!editFormData.draft}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, draft: !e.target.checked })
                }
              />
              Published (unpublished lessons stay in draft and don't notify users)
            </label>
          </div>
          <div className="form-actions">
            <button className="btn-save" onClick={handleSaveEdit}>
              Save Changes
            </button>
            <button className="btn-cancel" onClick={handleCancel}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        // Lessons List
        <PullToRefresh onRefresh={loadLessons}>
          {filteredLessons.length === 0 && searchQuery && (
            <div className="empty-state">No lessons match your search</div>
          )}
          {filteredLessons.length > 0 && (
          <div className="items-grid">
            {filteredLessons.map((lesson) => (
            <div key={lesson.id} className="item-card">
              <div style={{ display: "flex", alignItems: "flex-start", gap: "12px", marginBottom: "12px" }}>
                <div className="item-icon">
                  {SVG_ICONS[lesson.icon] ? (
                    SVG_ICONS[lesson.icon]({ size: 28, color: "#5B9EF0" })
                  ) : (
                    <span style={{ fontSize: "24px", color: "#7A9CC4" }}>?</span>
                  )}
                </div>
                <div className="item-content">
                  <h4 style={{ margin: "0 0 4px 0" }}>{lesson.title}</h4>
                  <p className="item-meta">
                    {console.log("[lesson-debug]", {title: lesson.title, draft: lesson.draft, schedule_date: lesson.schedule_date, scheduled: lesson.scheduled, addedAt: lesson.addedAt})}
                    {lesson.tag} • {lesson.dur} • {lesson.draft && lesson.schedule_date ? "Scheduled " + new Date(lesson.schedule_date).toLocaleDateString() : lesson.addedAt ? new Date(lesson.addedAt).toLocaleDateString() : ""}
                  </p>
                </div>
              </div>
              {lesson.subtitle && (
                <p className="item-subtitle">{lesson.subtitle}</p>
              )}
              <div style={{display: "flex", gap: "8px", marginTop: "8px"}}>
                {lesson.draft ? (
                  <div style={{display: "flex", alignItems: "center", gap: "4px", padding: "4px 12px", background: "rgba(245, 158, 11, 0.2)", color: "#F59E0B", borderRadius: "6px", fontSize: "12px", fontWeight: "600"}}>
                    {SVG_ICONS["alert-circle"]({ size: 14, color: "#F59E0B" })}
                    DRAFT
                  </div>
                ) : (
                  <div style={{display: "flex", alignItems: "center", gap: "4px", padding: "4px 12px", background: "#22C55E20", color: "#22C55E", borderRadius: "6px", fontSize: "12px", fontWeight: "600"}}>
                    {SVG_ICONS["check-circle"]({ size: 14, color: "#22C55E" })}
                    PUBLISHED
                  </div>
                )}
              </div>
              <div className="item-actions">
                <button
                  onClick={() => openPreview({
                    title: lesson.title,
                    subtitle: lesson.subtitle,
                    icon: lesson.icon,
                    tag: lesson.tag,
                    dur: lesson.dur,
                    body: typeof lesson.body === "string" ? lesson.body : "",
                  })}
                  title="Preview lesson"
                  style={{
                    padding: "6px 12px",
                    background: "rgba(59, 130, 246, 0.1)",
                    color: "#5B9EF0",
                    border: "1px solid rgba(59, 130, 246, 0.2)",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "14px",
                    transition: "all 0.2s",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = "rgba(59, 130, 246, 0.2)";
                    e.target.style.borderColor = "rgba(59, 130, 246, 0.4)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "rgba(59, 130, 246, 0.1)";
                    e.target.style.borderColor = "rgba(59, 130, 246, 0.2)";
                  }}
                >
                  {SVG_ICONS["eye"]({ size: 18, color: "#5B9EF0" })}
                </button>
                <button
                  onClick={() => handleToggleDraft(lesson)}
                  title={lesson.draft ? "Publish lesson" : "Revert to draft"}
                  style={{
                    padding: "6px 12px",
                    background: lesson.draft ? "rgba(34, 197, 94, 0.1)" : "rgba(245, 158, 11, 0.1)",
                    color: lesson.draft ? "#22C55E" : "#F59E0B",
                    border: lesson.draft ? "1px solid rgba(34, 197, 94, 0.2)" : "1px solid rgba(245, 158, 11, 0.2)",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "14px",
                    transition: "all 0.2s",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "4px",
                  }}
                  onMouseEnter={(e) => {
                    if (lesson.draft) {
                      e.target.style.background = "rgba(34, 197, 94, 0.2)";
                      e.target.style.borderColor = "rgba(34, 197, 94, 0.4)";
                    } else {
                      e.target.style.background = "rgba(245, 158, 11, 0.2)";
                      e.target.style.borderColor = "rgba(245, 158, 11, 0.4)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (lesson.draft) {
                      e.target.style.background = "rgba(34, 197, 94, 0.1)";
                      e.target.style.borderColor = "rgba(34, 197, 94, 0.2)";
                    } else {
                      e.target.style.background = "rgba(245, 158, 11, 0.1)";
                      e.target.style.borderColor = "rgba(245, 158, 11, 0.2)";
                    }
                  }}
                >
                  {lesson.draft
                    ? SVG_ICONS["check"]({ size: 18, color: "#22C55E" })
                    : SVG_ICONS["circle-x"]({ size: 18, color: "#F59E0B" })}
                  <span style={{ fontSize: "12px" }}>
                    {lesson.draft ? "Publish" : "Draft"}
                  </span>
                </button>
                <button
                  onClick={() => handleEdit(lesson)}
                  title="Edit lesson"
                  style={{
                    padding: "6px 12px",
                    background: "rgba(59, 130, 246, 0.1)",
                    color: "#5B9EF0",
                    border: "1px solid rgba(59, 130, 246, 0.2)",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "14px",
                    transition: "all 0.2s",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = "rgba(59, 130, 246, 0.2)";
                    e.target.style.borderColor = "rgba(59, 130, 246, 0.4)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "rgba(59, 130, 246, 0.1)";
                    e.target.style.borderColor = "rgba(59, 130, 246, 0.2)";
                  }}
                >
                  {SVG_ICONS["pencil"]({ size: 18, color: "#5B9EF0" })}
                </button>
                <button
                  onClick={() => handleDelete(lesson.id)}
                  title="Delete lesson"
                  style={{
                    padding: "6px 12px",
                    background: "rgba(59, 130, 246, 0.1)",
                    color: "#5B9EF0",
                    border: "1px solid rgba(59, 130, 246, 0.2)",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "14px",
                    transition: "all 0.2s",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = "rgba(59, 130, 246, 0.2)";
                    e.target.style.borderColor = "rgba(59, 130, 246, 0.4)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "rgba(59, 130, 246, 0.1)";
                    e.target.style.borderColor = "rgba(59, 130, 246, 0.2)";
                  }}
                >
                  {SVG_ICONS["trash"]({ size: 18, color: "#5B9EF0" })}
                </button>
              </div>
            </div>
            ))}
          </div>
          )}
        </PullToRefresh>
      )}
    </div>
  );
});

LessonListTab.displayName = 'LessonListTab';

export default LessonListTab;
